import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mockSubmissions, mockKhatians } from '../data/mockData';
import { StatusBadge } from '../components/StatusBadge';
import { FileUp, CheckCircle, Clock, AlertTriangle, Database, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
export const Dashboard: React.FC = () => {
  const {
    user
  } = useAuth();
  // Calculate stats based on mock data
  const pendingValidation = mockSubmissions.filter(s => s.status === 'pending_validation').length;
  const processing = mockSubmissions.filter(s => s.status === 'processing').length;
  const readyApproval = mockSubmissions.filter(s => s.status === 'ready_for_approval').length;
  const published = mockSubmissions.filter(s => s.status === 'published').length;
  const totalKhatians = mockKhatians.length;
  const StatCard = ({
    title,
    value,
    icon: Icon,
    colorClass,
    link
  }: any) => <div className="card p-6 flex items-center" data-id="element-331">
      <div className={`p-4 rounded-full ${colorClass} mr-4`} data-id="element-332">
        <Icon size={24} data-id="element-333" />
      </div>
      <div className="flex-1" data-id="element-334">
        <h3 className="text-sm font-medium text-slate-500" data-id="element-335">{title}</h3>
        <p className="text-2xl font-bold text-slate-900" data-id="element-336">{value}</p>
      </div>
      {link && <Link to={link} className="text-sm font-medium text-teal-600 hover:text-teal-800" data-id="element-337">
          View
        </Link>}
    </div>;
  const renderRoleSpecificContent = () => {
    switch (user?.role) {
      case 'operator':
      case 'zso':
        return <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-338">
              <StatCard title="My Submissions" value="12" icon={FileUp} colorClass="bg-blue-100 text-blue-600" link="/upload" data-id="element-339" />
              <StatCard title="Pending Validation" value={pendingValidation} icon={Clock} colorClass="bg-amber-100 text-amber-600" data-id="element-340" />
              <StatCard title="Published" value={published} icon={CheckCircle} colorClass="bg-teal-100 text-teal-600" data-id="element-341" />
            </div>
            <div className="card p-6" data-id="element-342">
              <div className="flex justify-between items-center mb-4" data-id="element-343">
                <h2 className="text-lg font-semibold text-slate-800" data-id="element-344">
                  Recent Submissions
                </h2>
                <Link to="/upload" className="btn-primary text-sm" data-id="element-345">
                  New Submission
                </Link>
              </div>
              <RecentSubmissionsTable limit={5} data-id="element-346" />
            </div>
          </>;
      case 'officer':
        return <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-347">
              <StatCard title="Pending Validation" value={pendingValidation} icon={AlertTriangle} colorClass="bg-amber-100 text-amber-600" link="/validate" data-id="element-348" />
              <StatCard title="Processing" value={processing} icon={Database} colorClass="bg-blue-100 text-blue-600" link="/processing" data-id="element-349" />
              <StatCard title="Reviewed Today" value="8" icon={CheckCircle} colorClass="bg-emerald-100 text-emerald-600" data-id="element-350" />
            </div>
            <div className="card p-6" data-id="element-351">
              <h2 className="text-lg font-semibold text-slate-800 mb-4" data-id="element-352">
                Action Required
              </h2>
              <RecentSubmissionsTable filterStatus="pending_validation" limit={5} data-id="element-353" />
            </div>
          </>;
      case 'admin':
        return <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-354">
              <StatCard title="Ready for Approval" value={readyApproval} icon={ShieldCheckIcon} colorClass="bg-indigo-100 text-indigo-600" link="/approve" data-id="element-355" />
              <StatCard title="Processing" value={processing} icon={Database} colorClass="bg-blue-100 text-blue-600" link="/processing" data-id="element-356" />
              <StatCard title="Total Published" value={published} icon={CheckCircle} colorClass="bg-teal-100 text-teal-600" data-id="element-357" />
              <StatCard title="Total Khatians" value={totalKhatians * 1000} icon={FileText} colorClass="bg-slate-100 text-slate-600" link="/viewer" data-id="element-358" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-359">
              <div className="card p-6" data-id="element-360">
                <h2 className="text-lg font-semibold text-slate-800 mb-4" data-id="element-361">
                  Pending Approvals
                </h2>
                <RecentSubmissionsTable filterStatus="ready_for_approval" limit={5} data-id="element-362" />
              </div>
              <div className="card p-6" data-id="element-363">
                <h2 className="text-lg font-semibold text-slate-800 mb-4" data-id="element-364">
                  System Status
                </h2>
                <div className="space-y-4" data-id="element-365">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-md border border-slate-100" data-id="element-366">
                    <span className="text-sm font-medium text-slate-700" data-id="element-367">
                      Middleware Service
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-368">
                      Operational
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-md border border-slate-100" data-id="element-369">
                    <span className="text-sm font-medium text-slate-700" data-id="element-370">
                      DLMS Sync
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-371">
                      Operational
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-md border border-slate-100" data-id="element-372">
                    <span className="text-sm font-medium text-slate-700" data-id="element-373">
                      Storage Capacity
                    </span>
                    <div className="w-1/2 bg-slate-200 rounded-full h-2.5" data-id="element-374">
                      <div className="bg-blue-600 h-2.5 rounded-full" style={{
                      width: '45%'
                    }} data-id="element-375"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>;
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto" data-id="element-376">
      <div className="mb-8" data-id="element-377">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-378">
          Welcome, {user?.name}
        </h1>
        <p className="text-slate-500" data-id="element-379">{user?.designation}</p>
      </div>
      {renderRoleSpecificContent()}
    </div>;
};
// Helper component for the dashboard
const ShieldCheckIcon = (props: any) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-380">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" data-id="element-381" />
    <path d="m9 12 2 2 4-4" data-id="element-382" />
  </svg>;
const RecentSubmissionsTable = ({
  filterStatus,
  limit
}: {
  filterStatus?: string;
  limit: number;
}) => {
  let data = mockSubmissions;
  if (filterStatus) {
    data = data.filter(s => s.status === filterStatus);
  }
  data = data.slice(0, limit);
  return <div className="overflow-x-auto" data-id="element-383">
      <table className="min-w-full divide-y divide-slate-200" data-id="element-384">
        <thead className="bg-slate-50" data-id="element-385">
          <tr data-id="element-386">
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-387">
              ID
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-388">
              Location
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-389">
              Khatians
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-390">
              Date
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-391">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200" data-id="element-392">
          {data.map(sub => <tr key={sub.id} className="hover:bg-slate-50" data-id="element-393">
              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900" data-id="element-394">
                {sub.id}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-395">
                {sub.mouja}, {sub.upazila}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-396">
                {sub.khatianCount}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-397">
                {new Date(sub.submittedAt).toLocaleDateString()}
              </td>
              <td className="px-4 py-3 whitespace-nowrap" data-id="element-398">
                <StatusBadge status={sub.status} data-id="element-399" />
              </td>
            </tr>)}
          {data.length === 0 && <tr data-id="element-400">
              <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500" data-id="element-401">
                No records found.
              </td>
            </tr>}
        </tbody>
      </table>
    </div>;
};